import { Model } from "@vuex-orm/core";
import { clientEntity } from "../entity/client-entity";
import { technicianEntity } from "../entity/technician-entity";
import { clientLocationEntity } from "../entity/client-location-entity";
import { contactEntity } from "../entity/contact-entity";
import { projectTypeEntity } from "./project-type-entity";
import { Enums } from "../../helper/enums";
import {
  getProjectStatusColor,
  getProjectStatusText,
  getProjectTaskStatusBadgeClass,
  getProjectTaskStatusHugeIcon,
  getProjectTaskStatusIconColor,
  getProjectTaskStatusText,
} from "../../helper/project-helper";
import {
  buildPortalCalendarDueDateView,
  formatPortalDateTime,
  type PortalCalendarDueDateView,
} from "../../helper/portal-datetime-format";
import type { HugeIconName } from "@/icons/icons";

function formatProjectUtcMed(iso: string): string {
  return formatPortalDateTime(iso);
}

export interface projectEntity {
  id: string;
  sections: ProjectSectionWithTasks[];
  number: number | null;
  numberWithPrefix: string;
  serviceProviderId: number;
  title: string;
  description: string;
  clientId: number;
  clientLocationId: number;
  leadTechnicianId: number | null;
  sharedWithContactIds: number[];
  typeId: number;
  tagIds: string[];
  groupIds: number[];
  dueDate: string | null;
  isActive: boolean;
  statusId: number;
  statusReason: string | null;
  createdById: number;
  createdOn: string;
  updatedById: number | null;
  updatedOn: string | null;
  recentActivity: any;
  tick: number;
  taskAssignees: number[];
  taskCompletionPercentage: number | null;
  client?: clientEntity | null;
  leadTechnician?: technicianEntity | null;
  projectType?: projectTypeEntity | null;
}

export type ProjectDueDateView = PortalCalendarDueDateView;

export type ProjectStatusView = {
  statusText: string;
  statusColor: string;
  statusHugeIcon: HugeIconName | null;
  progressPercent: number | null;
};

function formatListCountLabel(
  count: number,
  singular: string,
  plural: string,
): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export class projectEntity extends Model {
  static entity = "projectEntity";
  static primaryKey = "id";
  static fields() {
    return {
      id: this.string(""),
      sections: this.attr([]),
      number: this.number(null).nullable(),
      numberWithPrefix: this.string(""),
      serviceProviderId: this.number(0),
      title: this.string(""),
      description: this.string(""),
      clientId: this.number(0),
      clientLocationId: this.number(0),
      leadTechnicianId: this.number(null).nullable(),
      sharedWithContactIds: this.attr([]),
      typeId: this.string(""),
      tagIds: this.attr([]),
      groupIds: this.attr([]),
      dueDate: this.attr(null).nullable(),
      isActive: this.boolean(true),
      statusId: this.number(0),
      statusReason: this.string(null).nullable(),
      createdById: this.number(0),
      createdOn: this.attr(""),
      updatedById: this.number(null).nullable(),
      updatedOn: this.attr(null).nullable(),
      recentActivity: this.attr(null),
      tick: this.number(0),
      taskAssignees: this.attr([]),
      taskCompletionPercentage: this.number(null).nullable(),
      client: this.hasOne(clientEntity, "clientId", "clientId"),
      leadTechnician: this.hasOne(
        technicianEntity,
        "technicianId",
        "leadTechnicianId",
      ),
      projectType: this.hasOne(projectTypeEntity, "id", "typeId"),
    };
  }

  static count(): number {
    return this.query().count();
  }

  get customCreatedOn(): string {
    if (this.createdOn) {
      return formatProjectUtcMed(this.createdOn);
    }
    return "";
  }

  get customUpdatedOn(): string {
    if (this.updatedOn) {
      return formatProjectUtcMed(this.updatedOn);
    }
    return "";
  }

  get customDueDate(): string {
    if (this.dueDate) {
      return formatProjectUtcMed(this.dueDate);
    }
    return "";
  }

  get sharedWithContacts(): contactEntity[] {
    if (this.sharedWithContactIds && this.sharedWithContactIds.length > 0) {
      return contactEntity.query().whereIdIn(this.sharedWithContactIds).all();
    }
    return [];
  }

  get projectProgressInWholeNumber(): number {
    return Math.floor(this.taskCompletionPercentage || 0);
  }

  get displayId(): string {
    return this.numberWithPrefix || String(this.id);
  }

  get headerDisplayId(): string {
    return this.numberWithPrefix;
  }

  get dueDateView(): ProjectDueDateView {
    return buildPortalCalendarDueDateView(this.dueDate, {
      emptyLabel: "No target date",
      invalidLabel: this.dueDate ?? "",
    });
  }

  get statusView(): ProjectStatusView {
    const statusId = Number(this.statusId);
    let statusHugeIcon: HugeIconName | null = null;
    let progressPercent: number | null = null;

    if (statusId === Enums.ProjectStatus.InProgress) {
      progressPercent = Math.floor(Number(this.taskCompletionPercentage));
    } else if (statusId === Enums.ProjectStatus.NotStarted) {
      statusHugeIcon = "circleStroke";
    } else if (statusId === Enums.ProjectStatus.Completed) {
      statusHugeIcon = "checkmarkCircle02";
    } else if (statusId === Enums.ProjectStatus.OnHold) {
      statusHugeIcon = "minus01";
    } else if (statusId === Enums.ProjectStatus.Closed) {
      statusHugeIcon = "checkmarkCircle02";
    } else {
      statusHugeIcon = "circleStroke";
    }

    return {
      statusText: getProjectStatusText(statusId),
      statusColor: getProjectStatusColor(statusId),
      statusHugeIcon,
      progressPercent,
    };
  }

  get listSectionCount(): number {
    let count = 0;
    for (const section of this.sections ?? []) {
      if (section.isActive !== false) {
        count += 1;
      }
    }
    return count;
  }

  get listTaskCount(): number {
    let count = 0;
    for (const section of this.sections ?? []) {
      if (section.isActive === false) {
        continue;
      }
      if (Array.isArray(section.tasks)) {
        count += section.tasks.length;
      }
    }
    return count;
  }

  get listSectionColors(): string[] {
    const colors: string[] = [];
    for (const section of this.sections ?? []) {
      if (section.isActive === false) {
        continue;
      }
      if (
        colors.length < 3 &&
        typeof section.color === "string" &&
        section.color.length > 0
      ) {
        colors.push(section.color);
      }
    }
    return colors;
  }

  get listSectionCountLabel(): string {
    return formatListCountLabel(this.listSectionCount, "section", "sections");
  }

  get listTaskCountLabel(): string {
    return formatListCountLabel(this.listTaskCount, "task", "tasks");
  }

  get clientName(): string {
    if (this.clientLocationId) {
      return clientEntity.find(this.clientId)?.clientName || "";
    }
    return "";
  }

  get clientLocation(): string {
    if (this.clientLocationId) {
      return (
        clientLocationEntity.find(this.clientLocationId)?.clientLocationName ||
        ""
      );
    }
    return "";
  }

  get statusText(): string {
    return this.statusView.statusText;
  }
}

interface ProjectSectionWithTasks {
  id: string;
  title: string;
  color: string;
  tasks: ProjectTask[];
  isActive?: boolean;
}

export interface ProjectTaskChecklistSummary {
  percentageOfCompletedChecklistItems: number;
  totalItems: number;
  completedItems: number;
}

export interface ProjectTask {
  id: string;
  title: string;
  statusId: number;
  leadTechnicianId: number | null;
  secondaryTechnicianIds: number[];
  dueDate: string | null;
  priorityId: number;
  sortOrder: number;
  recentActivity: any;
  checklistDataSummary?: ProjectTaskChecklistSummary | null;
}
export type ProjectTaskAssigneeStack = {
  all: technicianEntity[];
  visible: technicianEntity[];
  overflow: technicianEntity[];
  overflowCount: number;
};

export class ProjectTaskView {
  id!: string;
  title!: string;
  statusId!: number;
  leadTechnicianId!: number | null;
  secondaryTechnicianIds!: number[];
  dueDate!: string | null;
  priorityId!: number;
  sortOrder!: number;
  recentActivity!: any;
  checklistDataSummary?: ProjectTaskChecklistSummary | null;

  constructor(task: ProjectTask) {
    this.id = task.id;
    this.title = task.title;
    this.statusId = task.statusId;
    this.leadTechnicianId = task.leadTechnicianId;
    this.secondaryTechnicianIds = task.secondaryTechnicianIds ?? [];
    this.dueDate = task.dueDate;
    this.priorityId = task.priorityId;
    this.sortOrder = task.sortOrder;
    this.recentActivity = task.recentActivity;
    this.checklistDataSummary = task.checklistDataSummary;
  }

  get progressPercent(): number {
    return Math.floor(
      this.checklistDataSummary?.percentageOfCompletedChecklistItems,
    );
  }

  get progressLabel(): string | undefined {
    const totalItems = this.checklistDataSummary?.totalItems;
    if (totalItems > 0) {
      return `${this.progressPercent}% of ${totalItems}`;
    }
  }

  get dueDateView(): ProjectDueDateView {
    return buildPortalCalendarDueDateView(this.dueDate, { emptyLabel: "" });
  }

  get statusText(): string {
    return getProjectTaskStatusText(this.statusId);
  }

  get statusBadgeClass(): string {
    return getProjectTaskStatusBadgeClass(this.statusId);
  }

  get statusIcon(): HugeIconName {
    return getProjectTaskStatusHugeIcon(this.statusId);
  }

  get statusIconColor(): string {
    return getProjectTaskStatusIconColor(this.statusId);
  }

  get assigneeIds(): number[] {
    const ids: number[] = [];
    if (this.leadTechnicianId) {
      ids.push(this.leadTechnicianId);
    }
    if (this.secondaryTechnicianIds?.length) {
      ids.push(...this.secondaryTechnicianIds);
    }
    return ids;
  }

  get assignees(): technicianEntity[] {
    return this.assigneeIds
      .map((technicianId) => technicianEntity.find(technicianId))
      .filter(
        (technician): technician is technicianEntity => technician != null,
      );
  }

  get assigneeStack(): ProjectTaskAssigneeStack {
    const maxVisible = 3;
    const all = this.assignees;
    const overflow = all.slice(maxVisible);
    return {
      all,
      visible: all.slice(0, maxVisible),
      overflow,
      overflowCount: overflow.length,
    };
  }

  get priorityIcon(): HugeIconName {
    switch (this.priorityId) {
      case Enums.ProjectPriority.Normal:
        return "normalSignal";
      case Enums.ProjectPriority.Low:
        return "lowSignal";
      case Enums.ProjectPriority.Urgent:
        return "alertSquare";
      case Enums.ProjectPriority.NoPriority:
        return "noPrioritySignal";
      case Enums.ProjectPriority.High:
        return "highSignal";
      default:
        return "noPrioritySignal";
    }
  }

  get priorityColor(): string {
    if (this.priorityId === Enums.ProjectPriority.Urgent) {
      return "#FA4B46";
    }
    return "var(--color-text-tertiary)";
  }

  get priorityText(): string {
    switch (this.priorityId) {
      case Enums.ProjectPriority.Normal:
        return "Normal";
      case Enums.ProjectPriority.Urgent:
        return "Urgent";
      case Enums.ProjectPriority.Low:
        return "Low";
      case Enums.ProjectPriority.High:
        return "High";
      case Enums.ProjectPriority.NoPriority:
        return "No Priority";
      default:
        return "";
    }
  }
}
